/**
 * Single owner for the OS biometric unlock prompt.
 *
 * AuthGate used to keep `biometricInFlightRef` on the component instance.
 * That failed in two production paths:
 *   1. Effect deps change while `BiometricStore.isEnabled()` is awaited — a
 *      second effect pass can start another `authenticateAsync` once the first
 *      finally clears the ref, or a remount resets the ref while the native
 *      prompt is still open.
 *   2. Credential login (password / OTP / OAuth) called `markCredentialUnlock`
 *      but the gate still awaited the OS prompt first, then checked the nonce
 *      — so a successful password entry was followed by a fingerprint prompt.
 *
 * Module-level state survives AuthGate remounts for the lifetime of the JS
 * runtime. One in-flight promise is shared by every concurrent caller; the
 * resolved user id skips a second challenge until logout.
 */

export type BiometricUnlockGateInput = {
  userId: string | null
  /** Live read — credential unlock can land while a preference read is in flight. */
  getCredentialUnlockNonce: () => number
  isBiometricEnabled: () => Promise<boolean>
  authenticate: () => Promise<boolean>
}

export type BiometricUnlockGateResult = {
  checked: true
  passed: true
  /** Whether this call invoked the OS prompt (vs short-circuit / join). */
  prompted: boolean
}

let inFlight: Promise<BiometricUnlockGateResult> | null = null
/** `undefined` = never resolved this runtime; `null` = logged-out pass-through. */
let resolvedUserId: string | null | undefined = undefined

/** Test-only: reset module mutex between cases. */
export function resetBiometricUnlockGateForTests(): void {
  inFlight = null
  resolvedUserId = undefined
}

/**
 * Resolve the biometric gate for the current auth identity.
 * Concurrent callers share one OS prompt; a second call for the same user
 * after success is a no-op.
 */
export async function resolveBiometricUnlockGate(
  input: BiometricUnlockGateInput,
): Promise<BiometricUnlockGateResult> {
  const { userId, getCredentialUnlockNonce, isBiometricEnabled, authenticate } = input

  if (!userId) {
    resolvedUserId = null
    return { checked: true, passed: true, prompted: false }
  }

  // Password / OTP / OAuth already unlocked this running session — never
  // stack an OS prompt on top of a credential the user just entered.
  if (getCredentialUnlockNonce() > 0) {
    resolvedUserId = userId
    return { checked: true, passed: true, prompted: false }
  }

  if (resolvedUserId === userId) {
    return { checked: true, passed: true, prompted: false }
  }

  if (inFlight) {
    return inFlight
  }

  inFlight = (async (): Promise<BiometricUnlockGateResult> => {
    try {
      // Re-check after joining the critical section — another caller may have
      // finished, or credentials may have landed while we waited for the lock.
      if (getCredentialUnlockNonce() > 0 || resolvedUserId === userId) {
        resolvedUserId = userId
        return { checked: true, passed: true, prompted: false }
      }

      const enabled = await isBiometricEnabled()
      if (!enabled) {
        resolvedUserId = userId
        return { checked: true, passed: true, prompted: false }
      }

      // Credential unlock may arrive while the preference read was in flight.
      if (getCredentialUnlockNonce() > 0 || resolvedUserId === userId) {
        resolvedUserId = userId
        return { checked: true, passed: true, prompted: false }
      }

      try {
        await authenticate()
      } catch {
        // Fail-open: bio is a convenience lock. Never eject a live session
        // solely because the OS prompt errored or timed out. Callers may wrap
        // authenticate with their own timeout; rejection is treated as cancel.
      }

      // Prefer a credential that landed during the prompt over re-prompting.
      resolvedUserId = userId
      return { checked: true, passed: true, prompted: true }
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}
